import { get, post } from "./api";

export type ChartAggregationFieldOption = {
  fieldId: string;
  label: string;
  dataType: string;
};

export type ChartAggregationDimensionsResponse = {
  dataType: string;
  groupByFields: ChartAggregationFieldOption[];
  segmentByFields: ChartAggregationFieldOption[];
};

export const fetchAggregationDimensions = async (
  projectId: string,
  dataType: string
): Promise<ChartAggregationDimensionsResponse> => {
  const { data } = await get({
    path: `/data/${projectId}/aggregation-dimensions/${dataType}`,
  });
  return data;
};

export const aggregateDataByFieldId = async (
  project: string,
  viewBy: string,
  segmentBy: string,
  dateUnit: string,
  filters: any,
  types: string[]
): Promise<any> => {
  const response = await post({
    path: `/data/${project}/aggregation/${viewBy}/${segmentBy}?dateUnit=${dateUnit}`,
    data: { filters, types },
  });
  return response.data;
};

export const aggregateFieldData = async (
  project: string,
  fieldId: string,
  objectType: string,
  fieldFunc: string,
  filters: any
): Promise<any> => {
  const response = await post({
    path: `/data/${project}/aggregation-field/${fieldId}/${fieldFunc}`,
    data: { filters, types: [objectType] },
  });
  return response.data;
};

export type ExportFormat = "csv" | "xlsx";

export const exportCSV = async (project: string, searchRequest: any): Promise<any> => {
  const params = encodeURIComponent(searchRequest.columns?.join(",") || "");
  return post({ path: `/data/${project}/export/csv?columns=${params}`, data: searchRequest });
};

/** Server export (CSV or Excel); uses blob response for correct binary handling. */
export const exportData = async (
  project: string,
  format: ExportFormat,
  searchRequest: any
): Promise<Blob> => {
  const params = encodeURIComponent(searchRequest.columns?.join(",") || "");
  const { data } = await post({
    path: `/data/${project}/export/${format}?columns=${params}`,
    data: searchRequest,
    responseType: "blob",
  });
  return data as Blob;
};

export function downloadBlob(blob: Blob, filename: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

export async function parseExportErrorMessage(error: any): Promise<string> {
  const fallback = error?.message || "Export failed";
  const blob = error?.response?.data;
  if (!(blob instanceof Blob)) {
    return fallback;
  }
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text) as { message?: string; error?: string };
    return parsed.message || parsed.error || text || fallback;
  } catch {
    return fallback;
  }
}

export function export2txt(csvData, filename) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(
    new Blob([csvData], {
      type: "text/csv",
    })
  );
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
